import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UserMembership {
  org_id: string;
  org_name: string;
  role: 'user' | 'client_admin' | 'exec' | 'admin';
  department: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  membership: UserMembership | null;
  profileName: string | null;
  onboardingCompleted: boolean | null;
  needsPasswordSetup: boolean | null;
  workflowsEnabled: boolean;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string, inviteCode: string, department: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  completeGoogleSignup: (orgId: string, department: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshMembership: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [membership, setMembership] = useState<UserMembership | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMembership = async (userId: string) => {
    const { data, error } = await supabase.rpc('get_user_membership', { _user_id: userId });
    if (error) {
      console.error('Error fetching membership:', error);
      return null;
    }
    if (data && data.length > 0) {
      return data[0] as UserMembership;
    }
    return null;
  };

  const fetchProfileStatus = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('onboarding_completed, needs_password_setup, full_name')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile status:', error);
      return { onboardingCompleted: false, needsPasswordSetup: false, fullName: null };
    }
    return {
      onboardingCompleted: data?.onboarding_completed ?? false,
      needsPasswordSetup: data?.needs_password_setup ?? false,
      fullName: data?.full_name ?? null,
    };
  };

  const refreshMembership = async () => {
    if (user) {
      const mem = await fetchMembership(user.id);
      setMembership(mem);
      const status = await fetchProfileStatus(user.id);
      setOnboardingCompleted(status.onboardingCompleted);
      setNeedsPasswordSetup(status.needsPasswordSetup);
      setProfileName(status.fullName);

    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Handle password recovery event - redirect to set new password
        if (event === 'PASSWORD_RECOVERY') {
          window.location.href = '/set-new-password';
          return;
        }
        
        // Set Sentry user context
        if (session?.user) {
          Sentry.setUser({ id: session.user.id, email: session.user.email });
        } else {
          Sentry.setUser(null);
        }

        // Defer membership fetch with setTimeout to avoid deadlock
        if (session?.user) {
          setTimeout(async () => {
            let mem = await fetchMembership(session.user.id);
            // Retry once if empty (handles race condition during signup)
            if (!mem) {
              await new Promise(r => setTimeout(r, 500));
              mem = await fetchMembership(session.user.id);
            }
            setMembership(mem);
            fetchProfileStatus(session.user.id).then(status => {
              setOnboardingCompleted(status.onboardingCompleted);
              setNeedsPasswordSetup(status.needsPasswordSetup);
              setProfileName(status.fullName);
        
            });
          }, 0);
        } else {
          setMembership(null);
          setOnboardingCompleted(null);
          setNeedsPasswordSetup(null);
          setProfileName(null);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchMembership(session.user.id).then(setMembership);
        fetchProfileStatus(session.user.id).then(status => {
          setOnboardingCompleted(status.onboardingCompleted);
          setNeedsPasswordSetup(status.needsPasswordSetup);
          setProfileName(status.fullName);
    
        });
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string, 
    password: string, 
    firstName: string,
    lastName: string,
    inviteCode: string, 
    department: string
  ): Promise<{ error: Error | null }> => {
    try {
      // First, look up the organization by invite code using secure RPC
      const { data: orgData, error: orgError } = await supabase.rpc(
        'lookup_org_by_invite_code', 
        { _invite_code: inviteCode }
      );

      if (orgError) {
        console.error('Invite code lookup error:', orgError);
        return { error: new Error('Invalid invite code. Please check and try again.') };
      }

      // RPC returns an array, get first result
      const org = orgData && orgData.length > 0 ? orgData[0] : null;
      if (!org) {
        return { error: new Error('Invalid invite code. Please check and try again.') };
      }

      // Create the auth user with metadata
      const fullName = `${firstName} ${lastName}`;
      const redirectUrl = `${window.location.origin}/prompt`;
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            first_name: firstName,
            last_name: lastName,
          }
        }
      });

      if (authError) {
        return { error: authError };
      }

      if (!authData.user) {
        return { error: new Error('Failed to create user account.') };
      }

      // Create the org membership
      const { error: membershipError } = await supabase
        .from('org_memberships')
        .insert({
          org_id: org.org_id,
          user_id: authData.user.id,
          role: 'user' as const,
          department: department as 'Business Development' | 'Consulting' | 'Strategy' | 'Events' | 'Ops' | 'Leadership',
        });

      if (membershipError) {
        console.error('Membership error:', membershipError);
        return { error: new Error('Account created but failed to join organization. Please contact support.') };
      }

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const completeGoogleSignup = async (
    orgId: string,
    department: string
  ): Promise<{ error: Error | null }> => {
    try {
      if (!user) {
        return { error: new Error('No authenticated user found.') };
      }

      const { error: membershipError } = await supabase
        .from('org_memberships')
        .insert({
          org_id: orgId,
          user_id: user.id,
          role: 'user' as const,
          department: department as 'Business Development' | 'Consulting' | 'Strategy' | 'Events' | 'Ops' | 'Leadership',
        });

      if (membershipError) {
        console.error('Google signup membership error:', membershipError);
        return { error: new Error('Failed to join organization. Please contact support.') };
      }

      await refreshMembership();
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signIn = async (email: string, password: string): Promise<{ error: Error | null }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setMembership(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      membership,
      profileName,
      onboardingCompleted,
      needsPasswordSetup,
      workflowsEnabled: true,
      loading,
      signUp,
      signIn,
      completeGoogleSignup,
      signOut,
      refreshMembership,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
